import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaCreditCard, FaQrcode, FaSpinner, FaSync } from 'react-icons/fa';
import { useAuth } from '../auth/AuthContext';
import { useCart } from '../contexts/CartContext';
import { storefrontPaymentService } from '../services/apiService';
import { formatPrice } from '../utils/currency';
import {
  buildRazorpayOptions,
  clearPendingCheckout,
  formatExpiryCountdown,
  getRazorpayKeyId,
  loadRazorpayScript,
  MAX_PAYMENT_RETRIES,
  readPendingCheckout,
  shouldShowUpiCollect,
} from '../utils/razorpayCheckout';

/**
 * B-07P — PaymentFlow: Razorpay integration after Task 09 order creation
 */
export default function PaymentFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { refreshCart } = useCart();

  const [session] = useState(() => readPendingCheckout(location.state));
  const [status, setStatus] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [opening, setOpening] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [processingMsg, setProcessingMsg] = useState('');
  const [upiCollect, setUpiCollect] = useState(null);
  const [countdown, setCountdown] = useState('');
  const autoOpenedRef = useRef(false);

  const orderId = session?.orderId;
  const razorpayOrderId = session?.razorpayOrderId;
  const amount = session?.amount;
  const amountPaise = useMemo(
    () => Math.round(Number(amount || 0) * 100),
    [amount]
  );

  const goToTracking = useCallback(
    (id) => {
      clearPendingCheckout();
      navigate(`/orders/${id}/track`, { replace: true, state: { paymentSuccess: true } });
    },
    [navigate]
  );

  const loadStatus = useCallback(async () => {
    if (!orderId) return null;
    try {
      const s = await storefrontPaymentService.getPaymentStatus(orderId);
      setStatus(s);
      setRetryCount(s?.paymentRetryCount || 0);
      if (s?.verified) goToTracking(orderId);
      return s;
    } catch {
      return null;
    }
  }, [orderId, goToTracking]);

  const verifyPayment = useCallback(
    async (response) => {
      setVerifying(true);
      setProcessingMsg('');
      try {
        const result = await storefrontPaymentService.verifyPayment({
          orderId,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_signature: response.razorpay_signature,
        });
        await refreshCart?.();
        toast.success('Payment successful!');
        goToTracking(result.orderId || orderId);
      } catch (err) {
        if (err.code === 'INVALID_SIGNATURE') {
          setRetryCount(err.retryCount || retryCount + 1);
          toast.error('Payment verification failed. Please retry.');
        } else if (err.code === 'PAYMENT_EXPIRED') {
          toast.error('Payment window expired.');
          navigate('/cart', { replace: true });
        } else {
          setProcessingMsg('Processing payment — you will be notified once confirmed.');
          toast.warning('Verifying payment… we will update your order shortly.');
          setTimeout(loadStatus, 3000);
        }
      } finally {
        setVerifying(false);
        setOpening(false);
      }
    },
    [orderId, retryCount, refreshCart, goToTracking, navigate, loadStatus]
  );

  const runDemoPay = useCallback(async () => {
    if (!orderId || !razorpayOrderId) return;
    setVerifying(true);
    try {
      const paymentId = `pay_demo_${Date.now()}`;
      const res = await storefrontPaymentService.createDemoSignature({
        razorpay_order_id: razorpayOrderId,
        razorpay_payment_id: paymentId,
      });
      await verifyPayment({
        razorpay_order_id: razorpayOrderId,
        razorpay_payment_id: paymentId,
        razorpay_signature: res.signature,
      });
    } catch (err) {
      toast.error(err.message || 'Demo payment failed');
      setVerifying(false);
    }
  }, [orderId, razorpayOrderId, verifyPayment]);

  const openRazorpay = useCallback(async () => {
    if (!orderId || !razorpayOrderId || opening || verifying) return;
    if (status?.expired) {
      toast.error('Payment window expired.');
      return;
    }
    if (shouldShowUpiCollect(retryCount)) {
      try {
        const data = await storefrontPaymentService.getUpiCollect(orderId);
        setUpiCollect(data);
      } catch {
        toast.error('Could not load UPI Collect fallback');
      }
      return;
    }

    const key = getRazorpayKeyId();
    if (!key) {
      toast.info('Razorpay key not configured — using demo payment.');
      await runDemoPay();
      return;
    }

    setOpening(true);
    setDismissed(false);
    try {
      await loadRazorpayScript();
      if (!window.Razorpay) throw new Error('Razorpay SDK unavailable');

      const options = buildRazorpayOptions({
        key,
        amountInPaise: amountPaise,
        currency: session?.currency || 'INR',
        razorpayOrderId,
        orderId,
        userPhone: user?.phone || user?.mobile,
        onSuccess: (res) => verifyPayment(res),
        onDismiss: () => {
          setOpening(false);
          setDismissed(true);
          toast.info('Complete payment to confirm your order.');
        },
        onFailure: async (resp) => {
          setOpening(false);
          const reason = resp?.error?.description || 'Payment failed';
          try {
            const fail = await storefrontPaymentService.recordFailure(orderId, reason);
            setRetryCount(fail?.retryCount || retryCount + 1);
            if (fail?.showUpiCollect) {
              const data = await storefrontPaymentService.getUpiCollect(orderId);
              setUpiCollect(data);
            }
          } catch {
            setRetryCount((c) => c + 1);
          }
          toast.error(reason);
        },
      });

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => options.onFailure?.(resp));
      rzp.open();
    } catch (err) {
      toast.error(err.message || 'Could not open payment');
      setOpening(false);
    }
  }, [
    orderId,
    razorpayOrderId,
    opening,
    verifying,
    status,
    retryCount,
    amountPaise,
    session,
    user,
    verifyPayment,
    runDemoPay,
  ]);

  useEffect(() => {
    if (!session?.orderId || !session?.razorpayOrderId) {
      toast.info('No pending payment. Redirecting to cart.');
      navigate('/cart', { replace: true });
      return;
    }
    loadStatus();
  }, [session, navigate, loadStatus]);

  useEffect(() => {
    const expiresAt = status?.expiresAt;
    if (!expiresAt) return undefined;
    const tick = () => setCountdown(formatExpiryCountdown(expiresAt));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [status?.expiresAt]);

  useEffect(() => {
    if (autoOpenedRef.current || !status || status.expired || status.verified) return;
    if (retryCount >= MAX_PAYMENT_RETRIES) return;
    autoOpenedRef.current = true;
    openRazorpay();
  }, [status, retryCount, openRazorpay]);

  if (!session) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b1120] text-slate-300">
        <FaSpinner className="animate-spin text-2xl text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1120] px-4 py-10 text-slate-100">
      <div className="mx-auto max-w-lg">
        <h1 className="text-2xl font-bold text-white">Complete payment</h1>
        <p className="mt-2 text-sm text-slate-400">
          Order <span className="font-mono text-amber-400">{orderId}</span>
        </p>

        <div className="mt-6 rounded-2xl border border-slate-700/60 bg-slate-800/50 p-6">
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Amount due</span>
            <span className="text-2xl font-bold text-amber-400">{formatPrice(amount)}</span>
          </div>

          {countdown && !status?.expired && (
            <p className="mt-3 text-xs text-amber-200/90">
              Complete payment within <strong>{countdown}</strong> or the order expires.
            </p>
          )}

          {dismissed && !status?.verified && (
            <div className="mt-4 rounded-xl border border-amber-700/40 bg-amber-950/25 px-4 py-3 text-sm text-amber-200">
              Payment was not completed. Your order stays open — pay now to confirm.
            </div>
          )}

          {processingMsg && (
            <div className="mt-4 rounded-xl border border-blue-700/40 bg-blue-950/25 px-4 py-3 text-sm text-blue-200">
              {processingMsg}
            </div>
          )}

          {upiCollect && (
            <div className="mt-6 rounded-xl border border-emerald-700/40 bg-emerald-950/20 p-4">
              <p className="flex items-center gap-2 font-semibold text-emerald-200">
                <FaQrcode /> UPI Collect fallback
              </p>
              <p className="mt-2 text-xs text-emerald-300/80">{upiCollect.instructions}</p>
              <p className="mt-3 font-mono text-sm text-white">{upiCollect.upiId}</p>
              <p className="mt-1 text-lg font-bold text-emerald-300">{formatPrice(upiCollect.amount)}</p>
            </div>
          )}

          <div className="mt-6 space-y-3">
            {!shouldShowUpiCollect(retryCount) && (
              <button
                type="button"
                onClick={openRazorpay}
                disabled={opening || verifying || status?.expired}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-4 text-base font-bold text-slate-900 hover:bg-amber-400 disabled:opacity-50"
              >
                {opening || verifying ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  <FaCreditCard />
                )}
                {verifying ? 'Verifying…' : opening ? 'Opening Razorpay…' : 'Pay now'}
              </button>
            )}

            {retryCount > 0 && retryCount < MAX_PAYMENT_RETRIES && (
              <p className="text-center text-xs text-slate-500">
                Retry {retryCount} of {MAX_PAYMENT_RETRIES}
              </p>
            )}

            {!getRazorpayKeyId() && (
              <button
                type="button"
                onClick={runDemoPay}
                disabled={verifying}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-600 py-3 text-sm font-semibold text-slate-300 hover:bg-slate-800"
              >
                Demo pay (dev)
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate('/cart')}
              className="flex w-full items-center justify-center gap-2 py-2 text-sm text-slate-500 hover:text-slate-300"
            >
              <FaSync className="text-xs" /> Back to cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
